import React, { Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from 'react-error-boundary';
import Layout from '@components/Layout/Layout';
import LoadingSpinner from '@components/Common/LoadingSpinner';
import ErrorFallback from '@components/Common/ErrorFallback';
import LoginPage from '@pages/Auth/LoginPage';
import ProtectedRoute from '@components/Auth/ProtectedRoute';
import { useAuth } from '@store/hooks/useAuth.ts';
import { authApi } from '@services/api.ts';
import { ForbiddenPage } from '@pages/ForbiddenPage/ForbiddenPage.tsx';
import { AdminPage } from '@pages/AdminPage/AdminPage.tsx';

const SpatialDataPage = React.lazy(
  () => import('@pages/SpatialData/SpatialDataPage')
);
const VisualizationPage = React.lazy(
  () => import('@pages/Visualization/VisualizationPage')
);
const UsersPage = React.lazy(() => import('@pages/Users/UsersPage'));
const NotFoundPage = React.lazy(() => import('@pages/NotFound/NotFoundPage'));

function App() {
  const { i18n } = useTranslation();
  const { user, setUser } = useAuth();
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    document.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    const initializeAuth = async () => {
      if (user) return;

      const accessToken = localStorage.getItem('accessToken');

      if (!accessToken) {
        setIsInitializing(false);
        return;
      }

      setIsInitializing(true);
      try {
        const response = await authApi.getCurrentUser();

        if (response.data) {
          setUser(response.data);
        }
      } catch (error) {
        console.error('Failed to load user:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeAuth();
  }, []);

  if (isInitializing) {
    return <LoadingSpinner />;
  }

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, errorInfo) => {
        console.error('Application Error:', error, errorInfo);
      }}
    >
      <div className="App min-h-screen bg-gray-50">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forbidden" element={<ForbiddenPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/visualization" replace />} />
              <Route path="spatial-data/*" element={<SpatialDataPage />} />
              <Route path="visualization/*" element={<VisualizationPage />} />
              <Route path="users/*" element={<UsersPage />} />
              <Route path="users/roles" element={<AdminPage />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </div>
    </ErrorBoundary>
  );
}

export default App;
